import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loginUser, refreshAuth, logout, exchangeAuthCode } from "./authThunks";

const initialState = {
  accessToken: null,
  userRole: null,
  userUsername: null,
  userEmail: null,
  userFirstName: null,
  userLastName: null,
  userLocationId: null,
  userId: null,
  isLoading: false,
  error: null,
  authorizedLocations: [],
  selectedRestaurant: null,
  subscriptionTier: null,
  accessTokenExpiresIn: null,
  authInitialized: false,
  refreshTokenTimeoutId: null,
  lastRefreshTime: null,
  userEnabled: true,
  userRoleId: null,
  refreshToken: null,
  user: null
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSelectedRestaurant: (state, action) => {
      state.selectedRestaurant = action.payload;
    },
    setRefreshTokenTimeoutId: (state, action) => {
      // Clear existing timeout if any
      if (state.refreshTokenTimeoutId) {
        clearTimeout(state.refreshTokenTimeoutId);
      }
      state.refreshTokenTimeoutId = action.payload;
    },
    clearAuth: (state) => {
      // Clear timeout if exists
      if (state.refreshTokenTimeoutId) {
        clearTimeout(state.refreshTokenTimeoutId);
      }
      return { ...initialState, authInitialized: true };
    },
    setAuthInitialized: (state, action) => {
      state.authInitialized = action.payload;
    },
    updateUserProfile: (state, action) => {
      const { firstname, lastname, email, username } = action.payload;
      state.userFirstName = firstname;
      state.userLastName = lastname;
      state.userEmail = email;
      state.userUsername = username;
    },
    loginStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess(state, action) {
      state.isLoading = false;
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
      state.refreshToken = action.payload.refreshToken;
      state.userRole = action.payload.user?.roles?.[0] || 'User';
    },
    loginFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    logoutUser(state) {
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.userRole = null;
      state.selectedRestaurant = null;
    },
    refreshStart(state) {
      state.isLoading = true;
    },
    refreshSuccess(state, action) {
      state.isLoading = false;
      state.accessToken = action.payload.accessToken;
      state.user = action.payload.user;
      state.refreshToken = action.payload.refreshToken;
      state.userRole = action.payload.user?.roles?.[0] || 'User';
      state.authInitialized = true;
    },
    refreshFailure(state) {
      state.isLoading = false;
      state.accessToken = null;
      state.user = null;
      state.refreshToken = null;
      state.userRole = null;
      state.selectedRestaurant = null;
      state.authInitialized = true;
    },
    exchangeCodeStart(state) {
      state.isLoading = true;
      state.error = null;
    },
    exchangeCodeSuccess(state, action) {
      // Populate state with both payload and individual fields for UI
      const { accessToken, refreshToken, user, authorizedLocations, selectedRestaurant, subscriptionTier, accessTokenExpiresIn } = action.payload;
      return {
        ...state,
        isLoading: false,
        accessToken,
        refreshToken,
        user,
        // Flattened user fields for UI components expecting them
        userRole: user.roles?.[0] || 'User',
        userId: user.id,
        userUsername: user.username,
        userEmail: user.email,
        userFirstName: user.firstName,
        userLastName: user.lastName,
        userLocationId: user.locationId,
        userEnabled: user.enabled,
        // Location & subscription
        authorizedLocations,
        selectedRestaurant,
        subscriptionTier,
        accessTokenExpiresIn,
        authInitialized: true,
        error: null,
        lastRefreshTime: Date.now(),
      };
    },
    exchangeCodeFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      state.accessToken = null;
      state.refreshToken = null;
      state.user = null;
      state.userRole = null;
      state.selectedRestaurant = null;
      state.authInitialized = true;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          isLoading: false,
          error: null,
          authInitialized: true,
          lastRefreshTime: Date.now()
        };
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Refresh cases
      .addCase(refreshAuth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(refreshAuth.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          isLoading: false,
          error: null,
          authInitialized: true
        };
      })
      .addCase(refreshAuth.rejected, (state, action) => {
        return {
          ...initialState,
          error: action.payload,
          authInitialized: true
        };
      })
      // Exchange Auth Code cases
      .addCase(exchangeAuthCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exchangeAuthCode.fulfilled, (state, action) => {
        // Populate state with both payload and individual fields for UI
        const { accessToken, refreshToken, user, authorizedLocations, selectedRestaurant, subscriptionTier, accessTokenExpiresIn } = action.payload;
        return {
          ...state,
          isLoading: false,
          accessToken,
          refreshToken,
          user,
          // Flattened user fields for UI components expecting them
          userRole: user.roles?.[0] || 'User',
          userId: user.id,
          userUsername: user.username,
          userEmail: user.email,
          userFirstName: user.firstName,
          userLastName: user.lastName,
          userLocationId: user.locationId,
          userEnabled: user.enabled,
          // Location & subscription
          authorizedLocations,
          selectedRestaurant,
          subscriptionTier,
          accessTokenExpiresIn,
          authInitialized: true,
          error: null,
          lastRefreshTime: Date.now(),
        };
      })
      .addCase(exchangeAuthCode.rejected, (state, action) => {
        return {
          ...state,
          isLoading: false,
          error: action.payload,
          accessToken: null,
          refreshToken: null,
          user: null,
          userRole: null,
          userId: null,
          userUsername: null,
          userEmail: null,
          userFirstName: null,
          userLastName: null,
          userLocationId: null,
          userEnabled: false,
          authInitialized: true,
        };
      })
      // Logout cases
      .addCase(logout.fulfilled, (state) => {
        return { ...initialState, authInitialized: true };
      });
  },
});

// Actions
export const { 
  setSelectedRestaurant, 
  setRefreshTokenTimeoutId,
  clearAuth,
  setAuthInitialized,
  updateUserProfile,
  loginStart,
  loginSuccess,
  loginFailure,
  logoutUser,
  refreshStart,
  refreshSuccess,
  refreshFailure,
  exchangeCodeStart,
  exchangeCodeSuccess,
  exchangeCodeFailure
} = authSlice.actions;

export default authSlice.reducer;